import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { useEffect, useMemo, useState } from 'react'
import { getPenalty, postPenalty } from 'services'
import { ConvertInputValue } from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { renderParagraph } from 'utils/RenderParagraph'
import { setLoanNumber } from 'utils/setLoanNumber'

import { paragraphs } from './constants'
import { declarationsPenaltyValidate } from './logic'

export function Penalties() {
  const [inputs, setInputs] = useState<any>({})
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<any>({})
  const [valueChanged, setValueChanged] = useState(false)
  const paragraphObj = paragraphs(inputs)

  useEffect(() => {
    setLoading(true)
    setLoanNumber()
    getPenalty()
      .then((res: any) => {
        if (res.success) {
          setData(res.data)
          const nInputs = declarationsPenaltyValidate(res.data)
          setInputs(nInputs)
        }
      })
      .finally(() => setLoading(false))
  }, [])

  const onChange = async (key: string, value: any) => {
    let temp = cloneDeep(data)
    temp[key] = value

    const radios: Record<string, string[]> = {
      noPrepaymentPenalty: ['anyPrepaymentPenalty', 'otherPrepaymentPenalty'],
      anyPrepaymentPenalty: ['noPrepaymentPenalty', 'otherPrepaymentPenalty'],
      otherPrepaymentPenalty: ['noPrepaymentPenalty', 'anyPrepaymentPenalty'],
    }
    if (radios[key] && value === true) {
      radios[key].forEach((k) => (temp[k] = false))
    }

    setData(temp)
    const nInputs = declarationsPenaltyValidate(temp)
    setInputs(nInputs)
    setValueChanged(true)
  }

  const onSubmit = async () => {
    const temp = cloneDeep(data)
    Object.keys(temp).forEach((key) => {
      temp[key] = ConvertInputValue(inputs[key], temp[key])
    })
    setLoading(true)
    await postPenalty(temp)
    setLoading(false)
    setValueChanged(false)
  }

  const percentageOptionElement = useMemo(() => {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <select
          className="py-0 pl-1 pr-6 text-[12.5px] leading-[16px] border-gray-300"
          value={data.percentageOption ?? '-'}
          onChange={(e) => {
            onChange('percentageOption', e.target.value)
          }}
          disabled={inputs.percentageOption?.disabled}
        >
          <option value="-" disabled>
            {' '}
          </option>
          <option value="Total Pmt">Total Pmt</option>
          <option value="P & I">P & I</option>
        </select>
      </div>
    )
  }, [data.percentageOption, inputs.percentageOption])

  const defaultAfterUnitElement = useMemo(() => {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <select
          className="py-0 pl-1 pr-6 text-[12.5px] leading-[16px] border-gray-300"
          value={data.defaultAfterUnit ?? '-'}
          onChange={(e) => {
            onChange('defaultAfterUnit', e.target.value)
          }}
          disabled={inputs.defaultAfterUnit?.disabled}
        >
          <option value="-" disabled>
            {' '}
          </option>
          <option value="Days">Days</option>
          <option value="Weeks">Weeks</option>
          <option value="Months">Months</option>
        </select>
      </div>
    )
  }, [data.defaultAfterUnit, inputs.defaultAfterUnit])

  return (
    <div>
      <LayoutLoading show={loading} />
      <SaveChanges show={valueChanged} label="Save Changes" onSave={onSubmit} />
      <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
        {Object.keys(inputs).map((key, index) => {
          const input = inputs[key]
          input.value = data[key]
          if (!input.visible) return
          if (key === 'percentage') input.additionalElements = percentageOptionElement
          if (key === 'defaultAfter') input.additionalElements = defaultAfterUnitElement
          return (
            <>
              <div key={index} className={`col-span-${input.span || 1} ${input.className}`}>
                <RenderInput input={input} Key={key} onChange={(key: string, value: any) => onChange(key, value)} />
              </div>
              {key === 'anyPrepaymentPenalty' && (
                <div className="flex flex-wrap items-end col-span-full pl-14 pr-5 pb-8">
                  {renderParagraph(paragraphObj, 0, onChange)}
                </div>
              )}
            </>
          )
        })}
      </div>
    </div>
  )
}
